// Public shared-report view (spec #22).
// At `/shared/reports/:id?token=...` anyone with the link can open
// `link`-visibility shares. Friends-shares need a logged-in viewer with
// a friendship to the owner; otherwise the backend returns 401/403.
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { sharesApi } from '@/api/sharesApi';
import { ApiException } from '@/api/errors';
import { formatDate, formatRelative, REPORT_STATUS_LABEL } from '@/utils/format';
import type { ResolvedShareReport } from '@/api/types';

export function SharedReportScreen() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const { getToken, isAuthenticated } = useAuth();
  const [share, setShare] = useState<ResolvedShareReport | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const token = params.get('token') ?? '';
    if (!token) {
      setError('Missing share token.');
      setErrorCode('SHARE_NOT_FOUND');
      setBusy(false);
      return;
    }
    let cancelled = false;
    async function load() {
      setBusy(true);
      setError(null);
      setErrorCode(null);
      try {
        const result = await sharesApi.resolve(getToken, id!, token);
        if (!cancelled) setShare(result);
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load this share.');
        setErrorCode(err?.code ?? null);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, params, getToken]);

  if (busy) {
    return (
      <div className="card">
        <div className="empty">Loading shared report…</div>
      </div>
    );
  }

  if (error || !share) {
    const isAuthRequired = errorCode === 'AUTH_REQUIRED' || errorCode === 'SHARE_FORBIDDEN';
    return (
      <div className="card">
        <div className="error-banner">{error ?? 'This share is not available.'}</div>
        {isAuthRequired && !isAuthenticated ? (
          <p style={{ marginTop: 16 }}>
            <Link to="/login" className="btn btn--primary">
              Sign in to view this report
            </Link>
          </p>
        ) : null}
        <p style={{ marginTop: 16 }}>
          Need to share one of your own reports? <Link to="/shares">Manage shares</Link>.
        </p>
      </div>
    );
  }

  const { report } = share;
  const title = report.title.trim() === '' ? '(Untitled report)' : report.title;

  return (
    <div className="stack">
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>
              Shared report
            </div>
            <h1 style={{ margin: '4px 0 0' }}>{title}</h1>
          </div>
          <span className="pill">{REPORT_STATUS_LABEL[report.status] ?? report.status}</span>
        </div>
        {report.owner?.username ? (
          <p className="muted" style={{ margin: '8px 0 0' }}>
            Shared by {report.owner.username} · {formatRelative(report.updated_at)}
          </p>
        ) : null}
      </div>

      <div className="card">
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {report.content.trim() === '' ? (
            <span className="muted">(No content)</span>
          ) : (
            report.content
          )}
        </div>
      </div>

      {share.is_owner ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            This is your report. <Link to={`/reports/${report.id}`}>Open in my reports</Link>.
          </p>
        </div>
      ) : null}

      <div className="muted" style={{ fontSize: 12 }}>
        Share created {formatDate(share.created_at)}
        {share.expires_at ? ` · expires ${formatDate(share.expires_at)}` : ''}.
      </div>
    </div>
  );
}
